import { EndNode, Section } from '@/components/ArticleElement'
import { IconButton, SliderArrows } from '@/components/buttons'
import { CompetitionCountdown, RecommendCompetitions } from '@/components/Competition'
import CompetitionPrice from '@/components/Competition/CompetitionPrice'
import SoldDrawnOverlay, { useSoldDrawnOverlay } from '@/components/Competition/SoldDrawnOverlay'
import { ImgFallback } from '@/components/Image'
import { InformationLayout } from '@/components/Layouts/Index'
import LazyDiv from '@/components/LazyDiv'
import LoadingSpinner from '@/components/LoadingSpinner'
import { ModalWithImage, ModalWithImageProps, QuickPicksModal, QuickPickItem } from '@/components/Modal'
import ProgressBar from '@/components/ProgressBar'
import { useLanguage, useLayout, useUser } from '@/hooks/Contexts'
import { API_URL } from '@/utils/Consts'
import { floatDivision, floatMultiplication } from '@/utils/FloatNumber'
import { Format } from '@/utils/Format'
import { svgs } from '@/utils/Images'
import { Action0, Action1 } from '@/utils/Types'
import { Competition, CompetitionDetail } from '@models/Index'
import * as Sentry from '@sentry/nextjs'
import { appUtils } from 'lib/AppUtils'
import axios from 'lib/axios'
import moment from 'moment'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Dispatch, FC, Fragment, memo, MutableRefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import Slider from 'react-slick'
import { toast } from 'react-toastify'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import { useSWRConfig } from 'swr'
const NumRangeInput = dynamic(() => import('@/components/FormElement/NumRangeInput'))
const NumberInput = dynamic(() => import('@/components/FormElement/NumberInput'))
const InstantWinTicketList = dynamic(() => import('@/components/Competition/InstantWinTicketList'))
const CompetitionTag = dynamic(() => import('@/components/Competition/CompetitionTag'))

const Detail = memo(function Detail({ data, isPreview }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { errors: errStr, cart: cartStr, competitions: compStr, navigation: navStr, orders: orderStr, terms_conditions } = useLanguage()
  const { isDesktop } = useLayout()
  const router = useRouter()
  const user = useUser()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [ticketAmount, setTickAmount] = useState(1)
  const [showFem, setShowFem] = useState<boolean>(false) // free-entry-method
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showQuickPickModal, setShowQuickPickModal] = useState<boolean>(false)

  const [modalData, setModalData] = useState<ModalWithImageProps>({
    type: 'success',
    title: '',
    imgUrl: '',
    alt: '',
    content: '',
    onConfirm: () => router.push('/cart'),
  })

  const [quickPickModalData, setQuickPickModalData] = useState<QuickPickItem[]>([])
  const [availableLeft, setAvailableLeft] = useState(0)

  const { mutate, cache } = useSWRConfig()

  const handleQuantityChange = (id: number, quantity: number) => {
    setQuickPickModalData(prev => prev.map(item =>
      item.id === id ? { ...item, quantity } : item
    ))
  }

  const handleQuickAdd = (id: number, quantity: number, event?: React.MouseEvent<HTMLButtonElement>) => {
    const item = quickPickModalData.find(item => item.id === id)
    if (item) {
      // Add to cart logic here
      event?.preventDefault()
      event?.stopPropagation()
      if (isLoading) {
        return
      }
      
      // Set loading state for this specific item
      setQuickPickModalData(prev => prev.map(item =>
        item.id === id ? { ...item, loading: true } : item
      ))
      
      axios
        .post(API_URL.addToCart, {
          cart_id: cache && cache.get(API_URL.cartCount)?.data ? cache.get(API_URL.cartCount).data.id : '',
          item: { competition_id: id, qty: quantity, quick_add: true },
        })
        .then(res => {
          if (res.data && res.data.data) {
            mutate(API_URL.cartCount, { ...res.data.data }, false)
            setQuickPickModalData(prev => prev.map(item =>
              item.id === id ? { 
                ...item, 
                added_quantity: (parseInt(item.added_quantity?.toString() || '0')) + parseInt(quantity.toString()), 
                recently_added: true, 
                loading: false 
              } : item
            ))
            setTimeout(() => {
              setQuickPickModalData(prev => prev.map(item =>
                item.id === id ? { ...item, recently_added: false } : item
              ))
            }, 2000)
          }
        })
        .catch(e => {
          const responseCode = e?.response?.data?.code
          toast.error(appUtils.handleApiError(e, errStr), { toastId: responseCode })
          // Reset loading state on error
          setQuickPickModalData(prev => prev.map(item =>
            item.id === id ? { ...item, loading: false } : item
          ))
        })
    }
  }

  const handleViewCart = () => {
    setShowModal(false)
    router.push('/cart')
  }

  useEffect(() => {
    if (
      appUtils.isEmpty(data)
      // || ((data.status != 1 || moment.utc(data.time).local().isBefore()) && !isPreview)
    ) {
      router.push('/404')
      return
    }
    const aLeft = Math.min(data.user_tickets_left ?? data.user_limit_amount, data.tickets_left)
    setTickAmount(Math.min(aLeft, data.default_ticket_amount ?? 1))
    setAvailableLeft(aLeft)
    if (typeof window?.['klaviyo']?.push != 'undefined') {
      const event = {
        CompetitionID: data.id,
        Store: 'UK',
        ProductName: data.title,
        CompetitionTickets: data.tickets_total,
        CompetitionWinners: 1 + data.has_instant_win,
        CompetitionDefaultTicketNumber: data.default_ticket_amount,
        CompetitionDrawDate: moment(data.draw_date).unix(),
        InstantWinComp: data.has_instant_win > 0 ? 'Yes' : 'No',
        InstantWinPrizes: data.has_instant_win,
        DefaultQuantity: data.default_ticket_amount,
        ItemPrice: data.sale_price || data.price,
        RowTotal: floatMultiplication(data.sale_price || data.price, data.default_ticket_amount),
        ProductURL: process.env.NEXT_PUBLIC_FRONTEND_URL + router.asPath,
        ImageURL: data.images?.[0] || '',
        ProductCategories: data.categories || [],
      }
      window['klaviyo'].push(['track', 'Viewed Product', event])
    }
  }, [data])

  const addToCart = e => {
    e.preventDefault()
    if (isLoading) {
      return
    }
    setIsLoading(true)
    axios
      .post(API_URL.addToCart, {
        cart_id: cache && cache.get(API_URL.cartCount)?.data ? cache.get(API_URL.cartCount).data.id : '',
        item: { competition_id: data.id, qty: ticketAmount },
      })
      .then(res => {
        if (res.data && res.data.data) {
          mutate(API_URL.cartCount, { ...res.data.data }, false)
          setAvailableLeft(availableLeft - ticketAmount)

          const popCompetitions = Array.isArray(res?.data?.data?.competitions) ? res.data.data.competitions : []
          const noTitle = !popCompetitions.every(competition => 
            !appUtils.isEmpty(competition.pop_title) || !appUtils.isEmpty(competition.title)
          )
          const noSalePrice = !popCompetitions.every(competition => 
            !appUtils.isEmpty(competition.sale_price)
          )
          const noPrice = !popCompetitions.every(competition => 
            !appUtils.isEmpty(competition.price)
          )
          const noBannerImage = !popCompetitions.every(competition => 
            !appUtils.isEmpty(competition.banner_image)
          )          

          if (popCompetitions.length > 0 && !noTitle && !noBannerImage && (!noSalePrice || !noPrice)) {
            toast.success(appUtils.getItemsStr(ticketAmount, cartStr) + cartStr.added_to_basket)
            // 手机端取前2个，pc端前3个competition
            const newItems = popCompetitions.slice(0, isDesktop ? 3 : 2).map((competition) => ({
              id: competition.id,
              image: competition.banner_image,
              title: competition.pop_title || competition.title,
              price: competition.sale_price || competition.price,
              quantity: Math.min(competition.default_ticket_amount || null, competition.tickets_left || null, competition.user_limit_amount || null, 5),
              max_quantity: Math.min(competition.tickets_left || null, competition.user_limit_amount || null),
              loading: false
            }))
            setQuickPickModalData(newItems)
            setShowQuickPickModal(true)
          } else {
            setModalData(prev => ({
              ...prev,
              type: 'success',
              content: appUtils.getItemsStr(ticketAmount, cartStr) + ' ' + cartStr.added_to_basket,
              onConfirm: () => router.push('/cart'),
            }))
            setShowModal(true)
          }
          if (typeof window?.['klaviyo']?.push != 'undefined') {
            const rowTotal = floatMultiplication(data.sale_price ?? data.price, ticketAmount)
            const event = {
              $event_id: `cart-${user?.id || 'guest'}-${moment().unix()}`,
              $value: res.data.data?.grant_total || rowTotal || 0,
              ItemNames: [data.title],
              CheckoutURL: process.env.NEXT_PUBLIC_FRONTEND_URL + '/cart',
              Items: [
                {
                  CompetitionID: data.id,
                  Store: 'UK',
                  ProductName: data.title,
                  CompetitionTickets: data.tickets_total,
                  CompetitionWinners: 1 + data.has_instant_win,
                  CompetitionDefaultTicketNumber: data.default_ticket_amount,
                  CompetitionDrawDate: moment(data.draw_date).unix(),
                  InstantWinComp: data.has_instant_win > 0 ? 'Yes' : 'No',
                  InstantWinPrizes: data.has_instant_win,
                  Quantity: ticketAmount,
                  ItemPrice: data.sale_price || data.price,
                  RowTotal: rowTotal,
                  ProductURL: process.env.NEXT_PUBLIC_FRONTEND_URL + router.asPath,
                  ImageURL: data.images?.[0] || '',
                  ProductCategories: data.categories || [],
                },
              ],
            }
            window['klaviyo'].push(['track', 'Added to Cart', event])
          }
        }
      })
      .catch(e => {
        const responseCode = e?.response?.data?.code
        toast.error(appUtils.handleApiError(e, errStr), { toastId: responseCode })
        if (responseCode == '6007' && e.response?.data.data) {
          setAvailableLeft(e.response?.data.data.qty)
          setTickAmount(e.response?.data.data.qty)
        }
        if (responseCode == '4002' && !isPreview) {
          mutate(API_URL.cartCount)
          router.push('/404')
        }
      })
      .finally(() => setIsLoading(false))
  }

  const soldOrDrawn = useSoldDrawnOverlay(data.status, data.tickets_left)

  return (
    data &&
    !appUtils.isEmpty(data) && (
      <Fragment>
        <MetaTags data={data} />
        <div id='comp-detail' className='mt-88 w-screen bg-white/100 md:sticky md:top-0 md:z-[1] md:mt-0 md:shadow-lg md:shadow-stone-400/25'>
          {isDesktop && soldOrDrawn ? <SoldDrawnOverlay soldOrDrawn={soldOrDrawn} className='z-[2] text-2xl font-semibold' /> : null}
          {isDesktop && (
            <div className='container-gray flex flex-col gap-4 py-4 md:flex-row '>
              <div className="flex flex-col ${isDesktop ? 'justify-end' : 'justify-center'} w-5/12">
                <div className='mb-2 font-semibold text-xl'>{data.title}</div>
              </div>
              <div className="flex min-w-max flex-col  ${isDesktop ? '' : 'justify-end'} whitespace-nowrap border-t border-cyan-v2 pt-4 text-sm/4 md:border-t-0 md:pt-0">
                <div className='-mb-2 flex flex-col flex-wrap'>
                  <div className='flex flex-row flex-wrap items-center'>
                    <CompetitionPrice competition={data} extraCls={`mb-2 mr-4 lg:mr-6`} monospace='text-2xl' />
                    {data.tag && isDesktop ? <CompetitionTag tag={data.tag} className='mb-2 mr-4 lg:mr-6 w-fit' /> : null}
                  </div>
                  <div className='flex flex-row flex-wrap items-center'>
                    {data.tag && !isDesktop ? <CompetitionTag tag={data.tag} className='mb-2 mr-4 lg:mr-6 w-fit' /> : null}
                    {[
                      { svgE: svgs.ticket, box: '0 0 9 9', highlight: data.tickets_sold + ' / ' + data.tickets_total, text: compStr.tickets },
                      {
                        svgE: svgs.prizes,
                        box: '0 0 58 58',
                        highlight: 1 + data.has_instant_win,
                        text: data.has_instant_win > 0 ? navStr.winners : compStr.winner,
                      },
                      { svgE: svgs.user, box: '4 4 28 28', highlight: data.user_limit_amount, text: compStr.ticket_limits[1], prepend: compStr.ticket_limits[0] },
                      { svgE: svgs.star, box: '0 0 12 12', highlight: data.has_instant_win, text: orderStr.instant_win_prize },
                    ].map(
                      (item, index) =>
                        !appUtils.isEmpty(item.highlight) && (
                          <div key={index} className='mb-2 mr-4 flex flex-row items-center text-sm/4 last:mr-0 lg:mr-6'>
                            <svg className='mr-1 h-4 w-4 text-cyan-v2' fill='currentColor' viewBox={item.box} xmlns='http://www.w3.org/2000/svg'>
                              {item.svgE}
                            </svg>
                            {item.prepend}
                            <span className={item.prepend ? 'mx-1' : 'mr-1'}>{item.highlight}</span>
                            {item.text}
                          </div>
                        )
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isDesktop && (
            <div className='container-gray flex flex-col justify-between gap-4 py-4 md:flex-row'>
              <div className='flex flex-col justify-between'>
                <div className='mb-2 font-semibold'>{data.title}</div>
                <div className='-mb-2 flex flex-row flex-wrap items-center'>
                  <CompetitionPrice competition={data} extraCls='mb-2 mr-4 lg:mr-6' monospace='text-xl' />
                  {data.tag ? <CompetitionTag tag={data.tag} className='mb-2 mr-4 lg:mr-6' /> : null}
                  {[
                    { svgE: svgs.ticket, box: '0 0 9 9', highlight: data.tickets_sold + ' / ' + data.tickets_total, text: compStr.tickets },
                    {
                      svgE: svgs.prizes,
                      box: '0 0 58 58',
                      highlight: 1 + data.has_instant_win,
                      text: data.has_instant_win > 0 ? navStr.winners : compStr.winner,
                    },
                    { svgE: svgs.user, box: '4 4 28 28', highlight: data.user_limit_amount, text: compStr.ticket_limits[1], prepend: compStr.ticket_limits[0] },
                    { svgE: svgs.star, box: '0 0 12 12', highlight: data.has_instant_win, text: orderStr.instant_win_prize },
                  ].map(
                    (item, index) =>
                      !appUtils.isEmpty(item.highlight) && (
                        <div key={index} className='mb-2 mr-4 flex flex-row items-center text-sm/4 last:mr-0 lg:mr-6'>
                          <svg className='mr-1 h-4 w-4 text-cyan-v2' fill='currentColor' viewBox={item.box} xmlns='http://www.w3.org/2000/svg'>
                            {item.svgE}
                          </svg>
                          {item.prepend}
                          <span className={item.prepend ? 'mx-1' : 'mr-1'}>{item.highlight}</span>
                          {item.text}
                        </div>
                      )
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={`w-screen ${isDesktop ? 'bg-stone-100' : 'bg-white/100'}`}>
            <div className='container-gray flex flex-col md:items-stretch lg:flex-row lg:gap-x-4'>
              <div className='flex flex-col  pb-4 pt-4 md:pb-4 xl:min-w-[30rem]'>
                {/* progress bar and countdown */}
                <div className={`flex min-w-max flex-col justify-end whitespace-nowrap border-t border-cyan-v2 pt-4 gap-2 ${isDesktop ? 'text-lg/6' : 'text-lg/6'} md:border-t-0 md:pt-0`}>
                  <CompetitionCountdown endDate={data.draw_date} onFinish={() => router.reload()} />
                  <div className={`flex h-4 items-center gap-x-2  ${isDesktop ? 'w-2/3 mt-2' : 'w-full'}`}>
                    <span>{compStr.ticket_sold}</span>
                    <ProgressBar percentage={floatDivision(isPreview ? 0 : data.tickets_sold, data.tickets_total)} extraCls='h-3' pctCls='text-center' />
                  </div>
                </div>
              </div>
              <LazyDiv isActive={isDesktop} className='flex-1 pb-2 lg:py-4'>
                <TicketCtrl
                  data={data}
                  isPreview={isPreview}
                  isLoading={isLoading}
                  ticketAmount={ticketAmount}
                  setTickAmount={setTickAmount}
                  availableLeft={availableLeft}
                  addToCart={addToCart}
                  setShowFem={setShowFem}
                />
              </LazyDiv>
            </div>
          </div>
        </div>

        <LazyDiv
          isActive={typeof isDesktop != 'undefined' && !isDesktop}
          className='sticky top-16 z-[1] w-screen bg-stone-100 py-2 shadow-lg shadow-stone-400/25'
        >
          {!isDesktop && soldOrDrawn ? (
            <SoldDrawnOverlay.Top id='sold-drawn' topId='comp-detail' soldOrDrawn={soldOrDrawn} className='text-2xl font-semibold' />
          ) : null}
          <div className='container-gray'>
            <TicketCtrl
              data={data}
              isPreview={isPreview}
              isLoading={isLoading}
              ticketAmount={ticketAmount}
              setTickAmount={setTickAmount}
              availableLeft={availableLeft}
              addToCart={addToCart}
              setShowFem={setShowFem}
            />
          </div>
        </LazyDiv>

        {!Array.isArray(data.images) || data.images.length == 0 ? null : <SliderCtrl images={data.images} title={data.title} />}

        <div className='container-white flex flex-col'>
          {/* @ts-ignore */}
          <InformationLayout title={compStr.prizes_details} show={true} toggleProps={{ 'data-testid': 'btn-prizesDetails' }}>
            <div className='mt-4 whitespace-pre-wrap' dangerouslySetInnerHTML={{ __html: data.description }} />
          </InformationLayout>
          {data.has_instant_win > 0 ? (
            <InformationLayout
              cls='mt-4'
              title={orderStr.instant_win_prize}
              show={true}
              // @ts-ignore
              toggleProps={{ 'data-testid': 'btn-instantWinPrizes' }}
            >
              <InstantWinTicketList competitionId={data.id} descr={data.consolation_description} />
            </InformationLayout>
          ) : null}
          <InformationLayout cls='mt-4' show={showFem} title={compStr.free_entry_method.title}>
            {/* free-entry-method anchor */}
            <div id='free-entry-method' className='invisible absolute -top-76 h-0 w-0 sm:-top-68 md:-top-104 lg:-top-72' />
            <p className='mt-4'>{terms_conditions.paragraphs[2][14]['slice']?.(5)}</p>
            <ul className='mt-2 list-inside list-disc'>
              {terms_conditions.paragraphs[2]['toSpliced']?.(18, 5)
                .slice(15)
                .map((text: string, i) => (
                  <li key={i} className={i == 0 ? '' : i == 2 ? 'mt-2' : 'mt-1'}>
                    {text.slice(text.indexOf(') ') + 2)}
                    {i == 1 ? (
                      <ul className='ml-5 mt-1 list-inside list-[circle]'>
                        {terms_conditions.paragraphs[2].slice(18, 23).map((subTxt: string, j) => (
                          <li key={j}>{subTxt.slice(subTxt.indexOf(') ') + 2)}</li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                ))}
            </ul>
            <p className='mt-2'>
              <EndNode content={compStr.free_entry_method.outro} />
            </p>
          </InformationLayout>
          <InformationLayout cls='mt-4' title={navStr.terms_conditions}>
            <Section paragraphs={compStr.consent} extraCls='mt-4 text-sm text-black' paragraphCls='mb-2 last:mb-0' />
          </InformationLayout>
        </div>

        <RecommendCompetitions ids={data.id} extraCls='fluid-gray' titleCls='container-gray' bodyCls='container-gray' />

        <ModalWithImage
          show={showModal}
          onClose={() => {
            setShowModal(false)
            router.push('/competitions')
          }}
          {...modalData}
        />

        <QuickPicksModal
          show={showQuickPickModal}
          onClose={() => {
            setShowQuickPickModal(false)
            router.push('/competitions#main-content')
          }}
          items={quickPickModalData}
          onQuantityChange={handleQuantityChange}
          onQuickAdd={handleQuickAdd}
          onViewCart={handleViewCart}
        />
      </Fragment>
    )
  )
})

type TicketCtrlProps = {
  data: Competition
  isPreview: boolean
  isLoading: boolean
  ticketAmount: number
  availableLeft: number
  addToCart: Action1<any>
}
const TicketCtrl: FC<TicketCtrlProps & { setTickAmount: Dispatch<SetStateAction<number>>; setShowFem: Dispatch<SetStateAction<boolean>> }> = ({
  setTickAmount,
  setShowFem,
  ...props
}) => {
  const { isPreview, ticketAmount, availableLeft } = props
  const { competitions: compStr } = useLanguage()
  const { breakpoint } = useLayout()
  return (
    <div className='flex-1 whitespace-nowrap rounded-lg bg-white/60 px-4 pb-1 pt-4 shadow-xl shadow-stone-200/25 md:w-full md:pb-1 md:pt-2 xl:flex xl:flex-wrap xl:items-center'>
      <div className='contents xl:flex xl:flex-1 xl:flex-col xl:justify-center'>
        <NumRangeInput
          value={ticketAmount}
          max={availableLeft}
          onChange={setTickAmount}
          input={{
            'disabled': isPreview,
            // @ts-ignore
            'data-testid': 'slider-competition-ticketNum',
          }}
          className='h-7 w-full min-w-max'
          hideTip
        />
        <div className='flex items-center text-sm'>
          {availableLeft > 0 && (
            <NumberInput
              value={ticketAmount}
              max={availableLeft}
              onChange={setTickAmount}
              input={{
                'disabled': isPreview,
                // @ts-ignore
                'data-testid': 'inp-competition-ticketNum',
              }}
              className='mr-4 h-7'
            />
          )}
          <svg className='h-4 w-4 text-cyan-v2' fill='currentColor' viewBox='0 0 9 9' xmlns='http://www.w3.org/2000/svg'>
            {svgs.ticket}
          </svg>
          <span data-testid='txt-competition-ticketNum'>&nbsp;{ticketAmount}&nbsp;</span>
          {compStr.tickets}
          <AddCartBtn isActive={breakpoint && !breakpoint.includes('xs')} {...props} extraCls='ml-auto' />
        </div>
      </div>
      <AddCartBtn isActive={breakpoint && breakpoint.includes('xs')} {...props} extraCls='mt-2' />

      {/* Scroll to free-entry-method anchor on the same page */}
      <div className='mt-4 flex w-full text-sm sm:justify-end'>
        <Link href={'#free-entry-method'} passHref>
          <a className='clickable-text' onClick={() => setShowFem(true)}>
            {compStr.free_entry_avail}
          </a>
        </Link>
      </div>
    </div>
  )
}

const AddCartBtn: FC<TicketCtrlProps & { isActive: boolean; extraCls?: string }> = ({
  isActive,
  data,
  isPreview,
  isLoading,
  ticketAmount,
  availableLeft,
  addToCart,
  extraCls,
}) => {
  const { errors: errStr, cart: cartStr } = useLanguage()
  const isRendered = useRef<boolean>(isActive) // the first render
  if (isActive && !isRendered.current) {
    isRendered.current = true
  }
  if (!isRendered.current) {
    return null
  }
  // Subsequent renders will only change the display css
  return (
    <button
      type='button'
      onClick={addToCart}
      disabled={isLoading || availableLeft <= 0 || ticketAmount <= 0 || isPreview}
      className={`${isActive ? 'btn-rd-amber btn-hover-amber btn-disabled-grey flex h-10 w-full items-center justify-center px-4 sm:w-auto lg:w-fit' : 'hidden'
        } ${extraCls || ''}`}
      data-testid='btn-competition-addBasket'
    >
      {availableLeft <= 0 ? (
        <span className='text-error'>{errStr.limit_reach}</span>
      ) : (
        <>
          {isLoading && <LoadingSpinner className='mr-3 h-5 w-5 text-amber-v1' />}
          {cartStr.add_to_basket}
          {!isLoading && (
            <span>
              <span className='px-2'>|</span>
              {Format.withCurrency((data.sale_price ?? data.price) * ticketAmount)}
            </span>
          )}
        </>
      )}
    </button>
  )
}

const SliderCtrl: FC<{ images: Array<string>; title: string }> = ({ images, title }) => {
  const mainSlider = useRef<Slider>(null)
  const [mainSliderIndex, setMainSliderIndex] = useState(0)
  const [showFullScreenSlider, setShowFullScreenSlider] = useState<boolean>(false)
  const toggleFullScreenSlider = () => {
    if (showFullScreenSlider) {
      mainSlider.current?.slickPlay?.()
    } else {
      mainSlider.current?.slickPause?.()
    }
    setShowFullScreenSlider(prev => !prev)
  }
  useEffect(() => setMainSliderIndex(0), [images])
  return (
    <Fragment>
      <FullScreenSlider show={showFullScreenSlider} items={images} onToggle={toggleFullScreenSlider} mainIndex={mainSliderIndex} title={title} />
      <CompetitionImageSlider
        mainSliderRef={mainSlider}
        items={images}
        onToggle={toggleFullScreenSlider}
        mainIndex={mainSliderIndex}
        setMainIndex={setMainSliderIndex}
        title={title}
      />
    </Fragment>
  )
}

type SliderProps = {
  items: string[]
  onToggle: Action0
  mainIndex?: number
  setMainIndex?: Action1<number>
  title: string
}

const FullScreenSlider: FC<SliderProps & { show: boolean }> = ({ items, onToggle, mainIndex, title, show }) => {
  const { isDesktop } = useLayout()
  const [isPanningDisabled, setIsPanningDisabled] = useState(true)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [localIndex, setLocalIndex] = useState(mainIndex)
  const [isFullScreenEnabled, setIsFullScreenEnabled] = useState(true)
  const sliderRef = useRef<Slider>(null)
  const zoomRef = useRef([])
  const imgRef = useRef([])

  useEffect(() => {
    // @ts-ignore
    setIsFullScreenEnabled(document.fullscreenEnabled || document.webkitFullscreenEnabled || document.msFullscreenEnabled)
    zoomRef.current = zoomRef.current.slice(0, items.length)
    imgRef.current = zoomRef.current.slice(0, items.length)
    if (items.length === 1) {
      setLocalIndex(0)
    }
  }, [items])

  useEffect(() => {
    setLocalIndex(mainIndex)
    if (show && items) {
      sliderRef.current.slickGoTo(mainIndex, true)
      zoomRef.current[mainIndex]?.resetTransform()
    }
    document.addEventListener('fullscreenchange', exitFullscreenHandler)
    document.addEventListener('mozfullscreenchange', exitFullscreenHandler)
    document.addEventListener('MSFullscreenChange', exitFullscreenHandler)
    document.addEventListener('webkitfullscreenchange', exitFullscreenHandler)
    return () => {
      document.removeEventListener('fullscreenchange', exitFullscreenHandler)
      document.removeEventListener('mozfullscreenchange', exitFullscreenHandler)
      document.removeEventListener('MSFullscreenChange', exitFullscreenHandler)
      document.removeEventListener('webkitfullscreenchange', exitFullscreenHandler)
    }
  }, [show])

  const exitFullscreenHandler = () => {
    // @ts-ignore
    if (!document.fullscreenElement && !document.webkitIsFullScreen && !document.mozFullScreen && !document.msFullscreenElement) {
      setIsFullScreen(false)
    }
  }
  const exitFullscreen = () => {
    // @ts-ignore
    if (document.fullscreenElement || document.webkitIsFullScreen || document.mozFullScreen || document.msFullscreenElement) {
      // @ts-ignore
      if (window.safari !== undefined) {
        // @ts-ignore
        document?.webkitExitFullscreen()
      } else {
        document?.exitFullscreen()
      }
      setIsFullScreen(false)
    }
  }
  const btnFullscreenHandler = () => {
    if (isFullScreen) {
      exitFullscreen()
    } else {
      //checks whether window is using Safari browser
      // @ts-ignore
      if (window.safari !== undefined && isFullScreenEnabled && document.body?.webkitRequestFullscreen) {
        //fullscreen api is partially supported in Safari v 15.4 with the use of "webkit-" prefix
        //source: https://caniuse.com/fullscreen
        // @ts-ignore
        document.body?.webkitRequestFullscreen()
        setIsFullScreen(true)
      } else {
        if (isFullScreenEnabled && document.body?.requestFullscreen) {
          document.body?.requestFullscreen()
          setIsFullScreen(true)
        }
      }
    }
  }
  const btnFullscreenExitHandler = () => {
    if (isFullScreen) {
      exitFullscreen()
    }
    if (zoomRef.current[localIndex]) {
      zoomRef.current[localIndex].resetTransform()
    }
    onToggle()
  }
  return (
    <div
      className={`fullscreen-slider fixed left-0 top-0 z-[53] h-screen w-screen bg-black py-12 transition-all duration-500 ${
        show ? 'translate-y-[0px] opacity-100' : 'pointer-events-none -translate-y-[10px] opacity-0'
      }`}
    >
      {/* z-index must be higher than the navbar's */}
      <div className='absolute left-0 top-0 z-10 flex w-screen items-center justify-between bg-black/30 p-4'>
        <span className='text-sm text-white/100'>
          {localIndex + 1} / {items.length}
        </span>
        <div className='flex gap-x-3'>
          <button
            aria-label='Zoom in'
            onClick={() => {
              imgRef.current[localIndex]?.focus?.()
              zoomRef.current[localIndex]?.zoomIn?.()
            }}
          >
            <svg xmlns='http://www.w3.org/2000/svg' className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='white' strokeWidth={2}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7' />
            </svg>
          </button>
          <button
            aria-label='Zoom out'
            onClick={() => {
              imgRef.current[localIndex]?.focus?.()
              zoomRef.current[localIndex]?.zoomOut?.()
            }}
          >
            <svg xmlns='http://www.w3.org/2000/svg' className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='white' strokeWidth={2}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7' />
            </svg>
          </button>
          {isFullScreenEnabled ? (
            <button aria-label='Toggle fullscreen' onClick={btnFullscreenHandler}>
              {isFullScreen ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-5 w-5'
                  viewBox='0 0 24 24'
                  strokeWidth='2'
                  stroke='white'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none'></path>
                  <polyline points='5 9 9 9 9 5'></polyline>
                  <line x1='3' y1='3' x2='9' y2='9'></line>
                  <polyline points='5 15 9 15 9 19'></polyline>
                  <line x1='3' y1='21' x2='9' y2='15'></line>
                  <polyline points='19 9 15 9 15 5'></polyline>
                  <line x1='15' y1='9' x2='21' y2='3'></line>
                  <polyline points='19 15 15 15 15 19'></polyline>
                  <line x1='15' y1='15' x2='21' y2='21'></line>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-5 w-5'
                  viewBox='0 0 24 24'
                  strokeWidth='2'
                  stroke='white'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none'></path>
                  <polyline points='16 4 20 4 20 8'></polyline>
                  <line x1='14' y1='10' x2='20' y2='4'></line>
                  <polyline points='8 20 4 20 4 16'></polyline>
                  <line x1='4' y1='20' x2='10' y2='14'></line>
                  <polyline points='16 20 20 20 20 16'></polyline>
                  <line x1='14' y1='14' x2='20' y2='20'></line>
                  <polyline points='8 4 4 4 4 8'></polyline>
                  <line x1='4' y1='4' x2='10' y2='10'></line>
                </svg>
              )}
            </button>
          ) : null}
          <button aria-label='Exit fullscreen' onClick={btnFullscreenExitHandler}>
            <svg xmlns='http://www.w3.org/2000/svg' className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='white' strokeWidth='2'>
              <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
        </div>
      </div>

      {/* Prev and Next buttons */}
      <div className='relative z-40 flex w-screen'>
        {items.length > 1 && (
          <Fragment>
            <button aria-label='Previous slide' className='slider-arrow left-4' onClick={() => sliderRef.current?.slickPrev?.()}>
              <svg className='h-4 w-4' viewBox='0 0 24 24'>
                {svgs.arrow}
              </svg>
            </button>
            <button aria-label='Next slide' className='slider-arrow right-4' onClick={() => sliderRef.current?.slickNext?.()}>
              <svg className='h-4 w-4 rotate-180' viewBox='0 -3 24 24'>
                {svgs.arrow}
              </svg>
            </button>
          </Fragment>
        )}
        <Slider
          lazyLoad='ondemand'
          className='w-screen'
          ref={sliderRef}
          afterChange={number => setLocalIndex(number)}
          infinite={true}
          arrows={false}
          draggable={false}
          beforeChange={oldIndex => zoomRef.current[oldIndex]?.resetTransform?.()}
        >
          {items.map((item, index) => (
            <TransformWrapper
              key={index}
              ref={el => (zoomRef.current[index] = el)}
              panning={{ disabled: isDesktop ? false : isPanningDisabled }}
              doubleClick={{ disabled: !isDesktop }}
              onZoomStop={ref => setIsPanningDisabled(ref.state.scale === 1)}
              centerZoomedOut={true}
            >
              <TransformComponent
                wrapperStyle={{
                  margin: 'auto',
                  height: isDesktop ? 'calc(90vh)' : '100%',
                  width: isDesktop ? 'calc(100vw - 4rem)' : '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifySelf: 'center',
                  justifyContent: 'center',
                }}
                contentStyle={{ paddingLeft: isDesktop ? '20%' : '0', paddingRight: isDesktop ? '20%' : '0' }}
              >
                <img
                  ref={el => (imgRef.current[index] = el)}
                  tabIndex={index}
                  src={item}
                  alt={title ? `${title} - image ${index + 1}` : item}
                  className='object-contain'
                />
              </TransformComponent>
            </TransformWrapper>
          ))}
        </Slider>
      </div>
    </div>
  )
}

const CompetitionImageSlider: FC<SliderProps & { mainSliderRef: MutableRefObject<Slider> }> = ({
  items,
  onToggle,
  mainIndex,
  setMainIndex,
  title,
  mainSliderRef,
}) => {
  const { isDesktop } = useLayout()
  const subSliderRef = useRef<Slider>(null)
  const [localIndex, setLocalIndex] = useState(mainIndex)
  useEffect(() => {
    setLocalIndex(0)
    setMainIndex(0)
    mainSliderRef.current?.slickGoTo?.(0)
    subSliderRef.current?.slickGoTo?.(0)
  }, [items])
  const prevClick = () => {
    if (!subSliderRef.current) {
      mainSliderRef.current?.slickPrev?.()
    } else {
      subSliderRef.current?.slickPrev?.()
    }
  }
  const nextClick = () => {
    if (!subSliderRef.current) {
      mainSliderRef.current?.slickNext?.()
    } else {
      subSliderRef.current?.slickNext?.()
    }
  }
  return (
    <div className={`absolute top-0 w-full bg-stone-900 bg-[url('/assets/bg-black.png')] bg-cover bg-no-repeat pt-16 md:relative md:pt-0`}>
      <div className={`flex h-72 w-full flex-row items-stretch md:h-[714px] ${isDesktop ? 'container-gray' : 'relative'}`}>
        {/* competition images */}
        <div className='relative w-4/5 flex-1'>
          {items.length > 1 ? (
            <Slider
              lazyLoad='ondemand'
              ref={mainSliderRef}
              // autoplay
              asNavFor={subSliderRef.current}
              infinite={true}
              afterChange={number => {
                if (!isDesktop || items.length <= 6) {
                  setLocalIndex(number)
                  setMainIndex(number)
                }
              }}
              arrows={false}
              draggable={false}
              vertical={!!isDesktop}
              className='comp-images h-72 md:h-[714px]'
            >
              {items.map((item, index) => (
                <ImgFallback
                  key={index}
                  src={item}
                  className='pointer-events-none h-72 w-full select-none object-cover md:h-[714px] md:object-contain'
                  alt={title ? `${title} - image ${index + 1}` : item}
                />
              ))}
            </Slider>
          ) : (
            <ImgFallback src={items[0]} className='h-72 w-full select-none object-cover md:h-[714px] md:rounded md:object-contain' alt={title ?? items[0]} />
          )}

          <IconButton
            aria-label='Toggle fullscreen'
            className='absolute right-2 top-4 flex h-8 w-8 items-center justify-center rounded p-1.5 text-cyan-v2 sm:right-4 md:right-0'
            onClick={onToggle}
          >
            {svgs.search}
          </IconButton>
          {items.length > 3 ? (
            <div className='absolute left-2 top-4 w-max rounded bg-stone-600 p-1 text-xs text-white/100 sm:left-4 md:left-0'>
              {localIndex + 1}/{items.length}
            </div>
          ) : null}
        </div>

        {/* competition images navigation */}
        {items.length < 2 ? null : isDesktop ? (
          <div
            className={`relative w-40 py-14 h-[714px]${items.length > 3 ? '' : items.length > 2 ? ' flex flex-col items-center justify-center' : ' hidden'}`}
          >
            <SliderArrows className='absolute inset-x-0 inset-y-4 flex-col justify-between' btnCls='rotate-90' prevClick={prevClick} nextClick={nextClick}>
              {items.length <= 6 ? (
                items.map((item, index) => (
                  <button
                    className={`flex justify-center overflow-clip rounded border-2 ${
                      index === localIndex ? 'border-amber-v1' : 'scale-95 border-transparent opacity-70'
                    }`}
                    key={index}
                    onClick={() => {
                      setLocalIndex(index)
                      setMainIndex(index)
                      mainSliderRef.current?.slickGoTo?.(index)
                    }}
                  >
                    <ImgFallback src={item} className='max-h-20 w-full select-none object-contain' alt={title ? `${title} - image ${index + 1}` : item} />
                  </button>
                ))
              ) : (
                <Slider
                  lazyLoad='ondemand'
                  ref={subSliderRef}
                  asNavFor={mainSliderRef.current}
                  slidesToShow={7}
                  centerMode={true}
                  slidesToScroll={1}
                  beforeChange={(curr, next) => {
                    mainSliderRef.current?.['innerSlider']?.['setState']?.({ initialSlide: 0, currentSlide: next, lazyLoadedList: [next] })
                  }}
                  afterChange={number => {
                    setLocalIndex(number)
                    setMainIndex(number)
                  }}
                  focusOnSelect={true}
                  arrows={false}
                  vertical={true}
                  infinite={true}
                  className='comp-images-nav h-[602px]'
                >
                  {items.map((item, index) => (
                    <button
                      className={`flex max-w-fit justify-center overflow-clip rounded border-2 ${
                        index === localIndex ? 'border-amber-v1' : 'scale-95 border-transparent opacity-50'
                      }`}
                      key={index}
                      onClick={() => {
                        setLocalIndex(index)
                        setMainIndex(index)
                        mainSliderRef.current?.slickGoTo?.(index)
                      }}
                    >
                      <ImgFallback src={item} className='max-h-20 w-full select-none object-contain' alt={title ? `${title} - image ${index + 1}` : item} />
                    </button>
                  ))}
                </Slider>
              )}
            </SliderArrows>
          </div>
        ) : (
          <SliderArrows className='absolute inset-x-2 inset-y-1/2 justify-between sm:inset-x-4' prevClick={prevClick} nextClick={nextClick} />
        )}
      </div>
    </div>
  )
}

const MetaTags = ({ data }: { data: CompetitionDetail }) => {
  const { navigation: navStr } = useLanguage()
  const description = useMemo(() => (data?.description ? data?.description.split(/[(\r\n)\r\n]+/)[0] : ''), [data?.description])
  const price = useMemo(() => data.sale_price?.toString() ?? data.price?.toString(), [data?.sale_price, data?.price])
  const currency = useMemo(() => appUtils.getDefaultCurrency(), [])
  return (
    <Head>
      <meta name='description' content={description} key='description' />
      {/* <meta property='og:description' content={description} key='og:description' /> */}
      <meta property='title' content={data.title} key='title' />
      <meta property='og:title' content={data.title} key='og:title' />
      <meta property='og:url' content={navStr.meta.domain + '/competitions/' + data.short_name} key='og:url' />
      {/* <meta property='og:type' content='product' key='og:type' /> */}
      <meta property='og:image' content={data.banner_image} />
      <meta property='og:image:alt' content={data.title} />
      <meta property='og:image:type' content='image/jpeg' />
      {/* <meta property='product:brand' content={navStr.app_name} /> */}
      <meta property='og:price:amount' content={price} />
      {/* <meta property='product:price:amount' content={price} /> */}
      <meta property='og:price:currency' content={currency} />
      {/* <meta property='product:price:currency' content={currency} />
      <meta property='product:availability' content='in-stock' />
      <meta property='product:condition' content='new' />
      <meta property='product:retailer_item_id' content={`${data.id}`} />
      {data.pixel_tags?.[0] ? <meta property='product:custom_label_0' content={data.pixel_tags[0]} /> : null}
      {data.pixel_tags?.[1] ? <meta property='product:custom_label_1' content={data.pixel_tags[1]} /> : null} */}
      <meta name='twitter:title' content={data.title} key='twitter:title' />
      <meta name='twitter:description' content={description} key='twitter:description' />
      <meta name='twitter:image' content={data.banner_image} />
      <meta name='title' content={`${data.title} - ${navStr.competitions} - ${navStr.app_name}`} key='title' />
      <title>{`${data.title} - ${navStr.competitions} - ${navStr.app_name}`}</title>
    </Head>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  try {
    const { shortName, token, isPreview, ticketsLeft } = context.query
    const res = await axios.get(API_URL.competitionDetail + '?short_name=' + shortName + (token ? '&token=' + token : ''), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'accept': 'application/json',
        'Content-Type': 'application/json',
        'cookie': context.req.headers.cookie || [],
      },
      withCredentials: true,
    })
    const data: CompetitionDetail = res.data.data
    const bool: Boolean = !!(isPreview?.toString() === '1')
    if (bool && !!ticketsLeft) {
      data.tickets_left = parseInt(ticketsLeft.toString())
    }
    return { props: { data, key: shortName, isPreview: bool } }
  } catch (e) {
    Sentry.captureException(e)
    return { props: { data: null } }
  }
}

export default Detail
